// Classify a recovery save error. PERMANENT = the encounter was SIGNED before the
// recovered dictation drained, so Transcript - Save routes to Error - Encounter
// Archived (notification metadata "error encounterArchived", output:error "The
// encounter cannot be changed once archived.", then process-interrupt). The save
// can NEVER succeed against an archived encounter, so the recovery loop must stop
// AND suppress the danger toast for a background recovery. We KEEP the local audio
// (do not delete) — it is un-attachable, not lost (spec §12.2/§14D). The
// DELETED-encounter case has no error-text signature of its own — it surfaces as
// an HTTP 404 on the save POST and is classified two-signal by
// isHttp404 + confirmEncounterGone below. Everything else defaults to
// TRANSIENT (retry).
const ARCHIVED_CODE = "error encounterArchived";
const ARCHIVED_TEXT = "the encounter cannot be changed once archived";
const NATIVE_CHAT_ID_RESOLUTION_ATTEMPTS = 31;
const NATIVE_CHAT_ID_RESOLUTION_DELAY_MS = 1000;

// parseRecording's answer when it REFUSED the save because the webview believes it
// is offline. That branch returns instead of throwing — deliberately, it is not an
// error — but a bare `return` made a refusal indistinguishable from an accepted
// upload (both resolve `undefined`).
//
// EVERY caller whose resolve path has side effects MUST check for this value:
// "resolved" here means "politely declined", and treating it as "saved" is how
// queued audio gets deleted (useIndexDb's drain), recoveries get marked done
// (useCrossDeviceRecovery), capsules get stranded (the native sync handler), and
// shells get left holding a dead "parsing" lease (useRecorder). Only a caller
// that does nothing on success may ignore it.
export const RECORDING_SAVE_DEFERRED_OFFLINE = "recording-save-deferred-offline";

// Extract ONLY a numeric HTTP status from an axios/interceptor error shape.
// Response/request bodies, config, and headers can carry PHI, so nothing but a
// number ever leaves here; a non-numeric status (e.g. "500") fails closed to null.
export function extractHttpStatus(error) {
  const status = error?.response?.status ?? error?.cause?.response?.status ?? error?.status;
  return typeof status === "number" ? status : null;
}

// PHI-safe diagnostic fields for a recording save/sync failure. The HTTP status
// and the error CLASS (e.name — "AxiosError"/"SyntaxError"/"TypeError"…) restore
// "why" a sync failed; callers pass `extra` (e.g. recordingId) for "which". The
// raw error and its message are deliberately excluded — they can echo request or
// response bodies that carry PHI. This is the single source of truth for what a
// recording-failure log line is allowed to contain.
//
// `code` joins them because a failure with no status at all is the commonest one in
// the field and the least diagnosable: the request never got an answer. ERR_NETWORK
// / ECONNABORTED / ETIMEDOUT is an enum from the HTTP client, carries nothing about
// the patient or the payload, and is the difference between "the connection dropped"
// and "the app is broken" when reading a log a week later.
export function describeRecordingSaveError(error, extra = {}) {
  const fields = { ...extra };
  const status = extractHttpStatus(error);
  if (status !== null) fields.status = status;
  const code = error?.code ?? error?.cause?.code;
  if (typeof code === "string") fields.code = code;
  // WHICH backend call failed. Tagged on the request (api.js config.meta) and lifted
  // by the interceptor, because every call shares one /web/run-process URL and the
  // processId lives in a body that carries clinical audio.
  const process = error?.process ?? error?.cause?.process;
  if (typeof process === "string") fields.process = process;
  // WHOSE backend. Each tenant has its own host, so this is the difference between
  // "our upload path is broken" and "one customer's server is down" — the exact
  // question LUCASHEALTH-V3 took two days to answer. A hostname, never a path or
  // query, so nothing from the request can ride along.
  const host = hostOf(error?.cause?.config?.baseURL || error?.config?.baseURL);
  if (host) fields.host = host;
  if (typeof error?.name === "string") fields.name = error.name;
  return fields;
}

function hostOf(baseURL) {
  if (typeof baseURL !== "string" || !baseURL) return null;
  try {
    return new URL(baseURL, "https://placeholder.invalid").hostname || null;
  } catch (_e) {
    return null;
  }
}

// Recordings already reported once. A failing upload is retried for as long as it
// takes — deliberately, since the queue never abandons audio — so reporting every
// attempt buried one recording's outage under 217 identical alerts (LUCASHEALTH-V3).
// Reporting NONE of them would be worse: an upload that cannot reach the server is
// exactly what we need to hear about. So: the first failure of a recording is an
// alert, and so is a failure that changes character (a connection refusal becoming a
// 500 is new information). The repeats in between are breadcrumbs.
const reportedSaveFailures = new Map();
const MAX_TRACKED_FAILURES = 200;

export function resetRecordingSaveFailureReportsForTests() {
  reportedSaveFailures.clear();
}

function isFirstOfItsKind(recordingId, fields) {
  if (!recordingId) return true; // nothing to deduplicate against
  const signature = `${fields.status ?? ""}|${fields.code ?? ""}|${fields.name ?? ""}`;
  if (reportedSaveFailures.get(recordingId) === signature) return false;
  if (reportedSaveFailures.size >= MAX_TRACKED_FAILURES) {
    reportedSaveFailures.delete(reportedSaveFailures.keys().next().value);
  }
  reportedSaveFailures.set(recordingId, signature);
  return true;
}

// An upload that cannot reach the server is reportable — that is audio not making it
// to the encounter, whatever the reason. What is NOT reportable is the same failure
// 217 times, which is what a two-day tenant outage produced in LUCASHEALTH-V3 while
// one recording retried.
//
// So the report is per RECORDING and per FAILURE KIND, not per attempt: the first
// failure alerts, a failure that changes character alerts again, and the repeats
// in between are breadcrumbs that still show up on whatever event follows.
//
// The fields carry what triage actually needs — the server's status, the transport
// code when there was no response at all, the backend process that failed, and the
// tenant host that failed it — and nothing else, because the raw error can echo
// request and response bodies carrying PHI.
export function logRecordingSaveFailure(error, recordingId, logger) {
  const fields = describeRecordingSaveError(error, recordingId ? { recordingId } : {});
  if (logger) {
    logger("Recording transcript save failed", fields);
    return;
  }
  const first = isFirstOfItsKind(recordingId, fields);
  // An expired session is the one exception: the clinician is already being told to
  // sign in again by the queue itself, and the fix is theirs, not ours.
  const alert = first && !error?.sessionExpired;
  (alert ? console.error : console.warn)("Recording transcript save failed", fields);
}

function classifyChatIdResolutionPending(error) {
  if (error && (typeof error === "object" || typeof error === "function")) {
    try {
      error.chatIdResolutionPending = true;
      if (error.chatIdResolutionPending === true) return error;
    } catch (_ignored) {
      // Frozen/non-extensible API errors cannot be tagged in place.
    }
  }

  const lookupError = new Error("Cannot resolve temporary chat ID yet");
  lookupError.chatIdResolutionPending = true;
  lookupError.cause = error;
  if (error?.sessionExpired) lookupError.sessionExpired = error.sessionExpired;
  return lookupError;
}

export function isPermanentRecoveryError(err) {
  if (!err) return false;
  const meta = err.notificationMetadata || err?.response?.data?.notificationMetadata;
  if (typeof meta === "string" && meta.indexOf(ARCHIVED_CODE) !== -1) return true;
  const outErr = err?.response?.data?.output?.error || err?.response?.data?.error || err?.outputError || "";
  if (typeof outErr === "string" && outErr.toLowerCase().indexOf(ARCHIVED_TEXT) !== -1) return true;
  return false;
}

// ── Deleted-encounter classification (the drain poison-pill) ────────────────
// A recording whose encounter was DELETED can never save: /web/run-process
// answers HTTP 404 and every retry 404s again, forever (prod 2026-07-02: the
// native drain re-offered recording 27 at 16:19Z and again at 16:52Z, each
// attempt a fresh 404 + support email). A bare 404 is NOT proof by itself —
// a missing/undeployed process also 404s, and an org switch changes which
// chats are visible — so classification is TWO-SIGNAL: the save 404'd AND a
// direct organization-chat fetch for the same chat 404s too. Anything
// ambiguous stays TRANSIENT (retry) — the same fail-safe default as
// isPermanentRecoveryError above.

// api.js's response interceptor wraps axios errors (e.cause = axios error);
// callers may also see a raw axios error. Accept both shapes.
export function isHttp404(err) {
  const status = err?.cause?.response?.status ?? err?.response?.status;
  return status === 404;
}

// Offline-created encounters are queued under a browser-only `temp-*` id. Once
// the encounter syncs, the server stores that id as `offlineId` and assigns the
// durable chat id that Transcript - Save requires. Resolve only temp ids; normal
// recordings stay on the existing path with no extra request. A missing mapping
// is transient (the encounter may still be syncing), so callers keep the audio
// queued and retry later.
export async function resolveRecordingChatId(getChatsByTempId, brainId, chatId) {
  if (typeof chatId !== "string" || !chatId.trim()) {
    throw new TypeError("chatId must be a non-empty string");
  }
  chatId = chatId.trim();
  if (!chatId.startsWith("temp-")) return chatId;
  let response;
  try {
    response = await getChatsByTempId(brainId, chatId);
  } catch (e) {
    // Preserve the original API error (notably sessionExpired) so the outer
    // drain keeps its existing user/session handling, while marking this as a
    // lookup-pending condition that must not consume the corrupt-audio give-up
    // budget used by finalless recovery.
    throw classifyChatIdResolutionPending(e);
  }
  const chats = response?.data?.chats;
  // A temp id is a per-recording UUID, so exactly one encounter should carry it as
  // its offlineId. If the lookup ever returns MORE than one, uploading to a guessed
  // chat could misattribute clinical audio to the wrong patient — treat it as
  // unresolved (retry, bounded by the caller's age ceiling) rather than pick one.
  if (Array.isArray(chats) && chats.length > 1) {
    const ambiguousError = new Error("Ambiguous temp ID " + chatId + " matched " + chats.length + " encounters");
    ambiguousError.chatIdResolutionAmbiguous = true;
    ambiguousError.chatIdResolutionPending = true;
    throw ambiguousError;
  }
  const resolvedChatId = chats?.[0]?.id;
  const durableChatId = typeof resolvedChatId === "string" ? resolvedChatId.trim() : "";
  if (!durableChatId || durableChatId.startsWith("temp-")) {
    const lookupError = new Error("Cannot find chat for temporary ID " + chatId);
    lookupError.chatIdResolutionPending = true;
    throw lookupError;
  }
  return durableChatId;
}

export async function resolveRecordingChatIdWithRetry(
  getChatsByTempId,
  brainId,
  chatId,
  {
    attempts = NATIVE_CHAT_ID_RESOLUTION_ATTEMPTS,
    delayMs = NATIVE_CHAT_ID_RESOLUTION_DELAY_MS,
    wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  } = {}
) {
  if (typeof getChatsByTempId !== "function") throw new TypeError("getChatsByTempId must be a function");
  if (!Number.isFinite(attempts) || !Number.isInteger(attempts) || attempts < 1) {
    throw new TypeError("attempts must be a finite integer of at least 1");
  }
  if (!Number.isFinite(delayMs) || delayMs < 0) {
    throw new TypeError("delayMs must be a finite number of at least 0");
  }
  if (typeof wait !== "function") throw new TypeError("wait must be a function");

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await resolveRecordingChatId(getChatsByTempId, brainId, chatId);
    } catch (error) {
      if (
        error?.sessionExpired ||
        error?.chatIdResolutionAmbiguous ||
        !error?.chatIdResolutionPending ||
        attempt === attempts - 1
      ) {
        throw error;
      }
      await wait(delayMs);
    }
  }
}

// Second signal: does the encounter itself still exist? `getChat` is injected
// (api.getChat) to keep this unit-testable. Returns true ONLY on a confirmed
// 404 of the chat fetch; success, network failure, 403, 5xx all return false —
// never classify as gone on ambiguity. A temp- chatId is an offline-created
// encounter whose server row may simply not exist YET — never "gone".
export async function confirmEncounterGone(getChat, chatId) {
  if (!chatId || String(chatId).startsWith("temp-")) return false;
  try {
    await getChat(chatId);
    return false;
  } catch (e) {
    return isHttp404(e);
  }
}
